import Placeholder from '@tiptap/extension-placeholder';
import TextAlign from '@tiptap/extension-text-align';
import Underline from '@tiptap/extension-underline';
import { EditorContent, useEditor } from '@tiptap/react';
import type { Editor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { DOCUMENT_TEMPLATES, type DocumentTemplateId } from './documentTemplates';

type Props = {
  initialHtml?: string;
  initialTitle?: string;
  saving?: boolean;
  saveLabel?: string;
  onSave: (payload: { title: string; html: string }) => void;
  onCancel?: () => void;
};

/**
 * Rich-text editor that produces sanitised HTML for the backend HTML→PDF
 * renderer. We keep the mark set small (bold/italic/underline + headings +
 * lists + alignment) because that's the surface the renderer supports — any
 * extra TipTap mark would be silently dropped at the server.
 */
export function DocumentEditor({
  initialHtml,
  initialTitle,
  saving,
  saveLabel,
  onSave,
  onCancel,
}: Props) {
  const { t } = useTranslation();
  const [title, setTitle] = useState(initialTitle ?? '');
  const [templateId, setTemplateId] = useState<DocumentTemplateId | ''>('');

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [1, 2, 3] },
      }),
      Underline,
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
      Placeholder.configure({
        placeholder: t('document_editor.placeholder') as string,
      }),
    ],
    content: initialHtml || '<p></p>',
    editorProps: {
      attributes: {
        class:
          'prose-like min-h-[420px] outline-none px-5 py-4 text-[14px] leading-relaxed text-gray-900 [&_h1]:text-[22px] [&_h1]:font-semibold [&_h1]:mt-3 [&_h2]:text-[17px] [&_h2]:font-semibold [&_h2]:mt-3 [&_h3]:text-[14px] [&_h3]:font-semibold [&_h3]:mt-2 [&_ul]:list-disc [&_ul]:pl-6 [&_ol]:list-decimal [&_ol]:pl-6 [&_p]:my-2',
      },
    },
  });

  if (!editor) return null;

  const applyTemplate = (id: DocumentTemplateId) => {
    const tpl = DOCUMENT_TEMPLATES.find((d) => d.id === id);
    if (!tpl) return;
    if (!title) setTitle(t(tpl.titleKey));
    editor.commands.setContent(tpl.html);
    setTemplateId(id);
  };

  const handleSave = () => {
    const html = editor.getHTML();
    onSave({ title: title.trim() || 'Untitled', html });
  };

  const wordCount = editor.getText().trim().split(/\s+/).filter(Boolean).length;

  return (
    <div className="card !p-0 overflow-hidden">
      <div className="px-4 py-3 border-b border-border bg-gray-50 flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-3">
        <input
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder={t('document_editor.title_placeholder') as string}
          className="flex-1 bg-white border border-border rounded px-3 py-2 text-[14px] font-medium"
          maxLength={200}
        />
        <select
          value={templateId}
          onChange={(e) => {
            const v = e.target.value as DocumentTemplateId;
            if (v) applyTemplate(v);
          }}
          className="bg-white border border-border rounded px-3 py-2 text-[13px]"
        >
          <option value="">{t('document_editor.template_picker')}</option>
          {DOCUMENT_TEMPLATES.map((tpl) => (
            <option key={tpl.id} value={tpl.id}>
              {t(tpl.titleKey)}
            </option>
          ))}
        </select>
      </div>

      <Toolbar editor={editor} />

      <EditorContent editor={editor} />

      <div className="px-4 py-3 border-t border-border bg-gray-50 flex items-center justify-between gap-3 flex-wrap">
        <div className="text-[11px] text-muted">
          {wordCount} {t('document_editor.words')}
        </div>
        <div className="flex items-center gap-2">
          {onCancel && (
            <button onClick={onCancel} className="btn btn-ghost btn-sm" disabled={saving}>
              {t('common.cancel')}
            </button>
          )}
          <button
            onClick={handleSave}
            disabled={saving || wordCount === 0}
            className="btn btn-primary btn-sm"
          >
            {saving
              ? t('document_editor.generating')
              : saveLabel ?? t('document_editor.save_as_pdf')}
          </button>
        </div>
      </div>
    </div>
  );
}

function Toolbar({ editor }: { editor: Editor }) {
  const { t } = useTranslation();
  return (
    <div className="px-3 py-2 border-b border-border bg-white flex flex-wrap items-center gap-1">
      <TbGroup>
        <TbBtn
          active={editor.isActive('heading', { level: 1 })}
          onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
          title={t('document_editor.heading1')}
        >
          H1
        </TbBtn>
        <TbBtn
          active={editor.isActive('heading', { level: 2 })}
          onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
          title={t('document_editor.heading2')}
        >
          H2
        </TbBtn>
        <TbBtn
          active={editor.isActive('heading', { level: 3 })}
          onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
          title={t('document_editor.heading3')}
        >
          H3
        </TbBtn>
        <TbBtn
          active={editor.isActive('paragraph')}
          onClick={() => editor.chain().focus().setParagraph().run()}
          title={t('document_editor.paragraph')}
        >
          ¶
        </TbBtn>
      </TbGroup>

      <TbDivider />

      <TbGroup>
        <TbBtn
          active={editor.isActive('bold')}
          onClick={() => editor.chain().focus().toggleBold().run()}
          title={t('document_editor.bold')}
        >
          <strong>B</strong>
        </TbBtn>
        <TbBtn
          active={editor.isActive('italic')}
          onClick={() => editor.chain().focus().toggleItalic().run()}
          title={t('document_editor.italic')}
        >
          <em>I</em>
        </TbBtn>
        <TbBtn
          active={editor.isActive('underline')}
          onClick={() => editor.chain().focus().toggleUnderline().run()}
          title={t('document_editor.underline')}
        >
          <u>U</u>
        </TbBtn>
      </TbGroup>

      <TbDivider />

      <TbGroup>
        <TbBtn
          active={editor.isActive('bulletList')}
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          title={t('document_editor.bullet_list')}
        >
          •
        </TbBtn>
        <TbBtn
          active={editor.isActive('orderedList')}
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          title={t('document_editor.ordered_list')}
        >
          1.
        </TbBtn>
      </TbGroup>

      <TbDivider />

      <TbGroup>
        <TbBtn
          active={editor.isActive({ textAlign: 'left' })}
          onClick={() => editor.chain().focus().setTextAlign('left').run()}
          title={t('document_editor.align_left')}
        >
          ⯇
        </TbBtn>
        <TbBtn
          active={editor.isActive({ textAlign: 'center' })}
          onClick={() => editor.chain().focus().setTextAlign('center').run()}
          title={t('document_editor.align_center')}
        >
          ☰
        </TbBtn>
        <TbBtn
          active={editor.isActive({ textAlign: 'right' })}
          onClick={() => editor.chain().focus().setTextAlign('right').run()}
          title={t('document_editor.align_right')}
        >
          ⯈
        </TbBtn>
      </TbGroup>

      <TbDivider />

      <TbGroup>
        <TbBtn onClick={() => editor.chain().focus().setHorizontalRule().run()} title={t('document_editor.hr')}>
          —
        </TbBtn>
        <TbBtn onClick={() => editor.chain().focus().undo().run()} title={t('document_editor.undo')}>
          ↶
        </TbBtn>
        <TbBtn onClick={() => editor.chain().focus().redo().run()} title={t('document_editor.redo')}>
          ↷
        </TbBtn>
      </TbGroup>
    </div>
  );
}

function TbGroup({ children }: { children: React.ReactNode }) {
  return <div className="flex items-center gap-0.5">{children}</div>;
}
function TbDivider() {
  return <div className="w-px h-5 bg-border mx-1" />;
}
function TbBtn({
  children,
  onClick,
  active,
  title,
}: {
  children: React.ReactNode;
  onClick: () => void;
  active?: boolean;
  title?: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      className={`w-8 h-8 rounded text-[13px] flex items-center justify-center transition-colors ${
        active ? 'bg-primary-50 text-primary' : 'text-gray-700 hover:bg-gray-100'
      }`}
    >
      {children}
    </button>
  );
}
